import React, { useCallback, useRef, useState } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Button, 
  Chip, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  TextField,
  Autocomplete,
  Divider,
  Snackbar,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ICategory, IBadge, IUser } from '../types/damTypes';
import { hasAllPermissions, hasPermission } from '../utils/permissionUtils';
import axiosInstance from '../services/axiosInstance';
import { API_BASE_URL } from '../config';
import { getBadges } from '../services/badgeService';

// Icons
import CategoryIcon from '@mui/icons-material/Category';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import ImageIcon from '@mui/icons-material/Image';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LabelIcon from '@mui/icons-material/Label';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BulkBarcodeDialog from '../modules/barcode/BulkBarcodeDialog';
import ProductImageValidationRunDialog from '../modules/productImageValidation/ProductImageValidationRunDialog';
import { useProductImageValidationRunner } from '../modules/productImageValidation/useProductImageValidationRunner';
import RelationImpactDialog from '../modules/referenceImpact/RelationImpactDialog';
import {
  getWordPressArchiveUnpublishImpacts,
  requestWordPressArchiveUnpublish,
  WordPressArchiveUnpublishImpact,
} from '../modules/wordpressPublishing/archiveUnpublishGuard';
import VisualEnrichmentBulkAction, {
  AthenaVisualProfileIcon,
  OPEN_ATHENA_VISUAL_ENRICHMENT_EVENT,
} from '../modules/knowledgeHub/VisualEnrichmentBulkAction';

type BadgeActionType = 'add' | 'remove';
type BadgeActionSelection = BadgeActionType | 'none';

interface BulkOperationBarProps {
  selectedAssetIds: string[];
  onClearSelection: () => void;
  categories: ICategory[];
  onOperationComplete: () => void;
  onOpenCsvSettings?: () => void;
  currentUser?: IUser | null;
  onHeightChange?: (height: number) => void;
}

const actionButtonSx = {
  textTransform: 'none',
  fontSize: '0.78rem',
  px: 1.25,
  py: 0.5,
  minHeight: 32,
  whiteSpace: 'nowrap',
  flexShrink: 0,
} as const;

const compactActionButtonSx = {
  ...actionButtonSx,
  fontSize: '0.76rem',
  px: 1,
} as const;

type BulkActionId =
  | 'badges'
  | 'downloadZip'
  | 'exportCsv'
  | 'barcode'
  | 'regenerateThumbnails'
  | 'generateAi'
  | 'athenaVisualProfile'
  | 'validoo'
  | 'validateProductImages'
  | 'manageCategories'
  | 'bulkEdit'
  | 'delete';

interface BulkActionDefinition {
  id: BulkActionId;
  permission: string | string[];
  label: string;
  compactLabel?: string;
  variant: 'outlined' | 'contained';
  color: 'primary' | 'error';
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
  compactPrimary: boolean;
  destructive?: boolean;
}

const BulkOperationBar: React.FC<BulkOperationBarProps> = ({
  selectedAssetIds,
  onClearSelection,
  categories,
  onOperationComplete,
  onOpenCsvSettings,
  currentUser,
  onHeightChange
}) => {
  const theme = useTheme();
  const isCompactTabletLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const barRef = useRef<HTMLDivElement | null>(null);
  const [isCategoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [isAiProcessing, setAiProcessing] = useState(false);
  const [aiProcessingMessage, setAiProcessingMessage] = useState<string | null>(null);
  const [isThumbnailProcessing, setThumbnailProcessing] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [operationResult, setOperationResult] = useState<{ success: boolean; message: string } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isQuickCategoryDialogOpen, setIsQuickCategoryDialogOpen] = useState(false);
  const [quickSelectedCategories, setQuickSelectedCategories] = useState<ICategory[]>([]);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [tagsInput, setTagsInput] = useState<string>('');
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renamePattern, setRenamePattern] = useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteImpactDialogOpen, setIsDeleteImpactDialogOpen] = useState(false);
  const [deleteWordPressImpacts, setDeleteWordPressImpacts] = useState<WordPressArchiveUnpublishImpact[]>([]);
  const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState(false);
  const [isBarcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [availableBadges, setAvailableBadges] = useState<IBadge[]>([]);
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const [badgeActions, setBadgeActions] = useState<Record<string, BadgeActionType>>({});
  const [badgeOverviewLoading, setBadgeOverviewLoading] = useState(false);

  // Regeneration progress dialog state
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenProgress, setRegenProgress] = useState<any | null>(null);
  const regenEsRef = useRef<EventSource | null>(null);
  const regenRunIdRef = useRef<string | null>(null);
  const regenPollIntervalRef = useRef<any>(null);
  const [regenLog, setRegenLog] = useState<string[]>([]);
  const [showRegenLog, setShowRegenLog] = useState<boolean>(false);
  const [regenTopToast, setRegenTopToast] = useState<{ success: boolean; message: string } | null>(null);

  // Manage Categories (new lightweight UX state)
  const [categoryMode, setCategoryMode] = useState<'add' | 'remove' | 'replace'>('add');
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Validoo sync state
  const [isValidooDialogOpen, setIsValidooDialogOpen] = useState(false);
  const [isValidooSyncing, setIsValidooSyncing] = useState(false);
  const [validooProgress, setValidooProgress] = useState<{ current: number; total: number; successCount: number; failedCount: number }>({ current: 0, total: 0, successCount: 0, failedCount: 0 });
  const [validooLog, setValidooLog] = useState<string[]>([]);
  const validooCancelRef = useRef(false);
  const [isValidooLogDialogOpen, setIsValidooLogDialogOpen] = useState(false);
  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<null | HTMLElement>(null);
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const handleProductImageValidationFinished = useCallback(() => {
    onOperationComplete();
  }, [onOperationComplete]);
  const productImageValidationRunner = useProductImageValidationRunner({
    onRunFinished: handleProductImageValidationFinished,
  });

  const closeMoreActionsMenu = () => setMoreActionsAnchorEl(null);

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Load per-category status across selected assets when dialog opens
  React.useEffect(() => {
    const loadOverview = async () => {
      if (!isCategoryDialogOpen || selectedAssetIds.length === 0) return;
      setOverviewLoading(true);
      try {
        const results = await Promise.all(
          selectedAssetIds.map(async (id) => {
            try {
              const resp = await axiosInstance.get(`/api/assets/${id}`);
              return Array.isArray(resp.data?.categories) ? resp.data.categories : [];
            } catch {
              return [];
            }
          })
        );
        const counts: Record<string, number> = {};
        results.forEach((cats: any[]) => {
          cats.forEach((c: any) => {
            const id = typeof c === 'string' ? c : (c?._id || c?.toString?.() || '');
            if (!id) return;
            counts[id] = (counts[id] || 0) + 1;
          });
        });
        setCategoryCounts(counts);
      } finally {
        setOverviewLoading(false);
      }
    };
    loadOverview();
  }, [isCategoryDialogOpen, selectedAssetIds]);

  React.useEffect(() => {
    if (!isBadgeDialogOpen) {
      setBadgeActions({});
      setBadgeCounts({});
      return;
    }
    loadBadges();
    loadBadgeOverview();
  }, [isBadgeDialogOpen, selectedAssetIds]);

  const getCategoryStatus = (categoryId: string): 'all' | 'some' | 'none' => {
    const cnt = categoryCounts[categoryId] || 0;
    if (cnt === 0) return 'none';
    if (cnt === selectedAssetIds.length) return 'all';
    return 'some';
  };

  const handleApplyCategories = async () => {
    if (selectedCategories.length === 0) return;
    
    try {
      setOperationResult(null);
      const categoryNames = selectedCategories.map(catId => 
        categories.find(cat => cat._id === catId)?.name || ''
      ).filter(name => name !== '');

      console.log('Making API call to categorize assets with:', {
        url: `${API_BASE_URL}/api/assets/bulk/categorize`,
        assetIds: selectedAssetIds,
        addCategories: categoryNames,
        removeCategories: []
      });

      const response = await axiosInstance.put(`/api/assets/bulk/categorize`, {
        assetIds: selectedAssetIds,
        addCategories: categoryNames,
        removeCategories: []
      });

      console.log('Categorize response:', response.data);
      
      setOperationResult({
        success: true,
        message: `Categories applied to ${response.data.modifiedCount} assets.`
      });
      
      setCategoryDialogOpen(false);
      setSelectedCategories([]);
      
      onOperationComplete();
    } catch (error: any) {
      console.error('Error applying categories:', error);
      setOperationResult({
        success: false,
        message: `Error applying categories: ${error.message}`
      });
    }
  };

  const handleRemoveCategories = async () => {
    if (selectedCategories.length === 0) return;
    
    try {
      setOperationResult(null);
      setIsRemoving(true);
      
      const categoryNames = selectedCategories.map(catId => 
        categories.find(cat => cat._id === catId)?.name || ''
      ).filter(name => name !== '');

      console.log('Making API call to remove categories with:', {
        url: `${API_BASE_URL}/api/assets/bulk/categorize`,
        assetIds: selectedAssetIds,
        addCategories: [],
        removeCategories: categoryNames
      });

      const response = await axiosInstance.put(`/api/assets/bulk/categorize`, {
        assetIds: selectedAssetIds,
        addCategories: [],
        removeCategories: categoryNames
      });

      console.log('Remove categories response:', response.data);

      setOperationResult({
        success: true,
        message: `Categories removed from ${response.data.modifiedCount} assets.`
      });
      
      setIsRemoving(false);
      
      onOperationComplete();
    } catch (error: any) {
      console.error('Error removing categories:', error);
      setOperationResult({
        success: false,
        message: `Error removing categories: ${error.message}`
      });
      setIsRemoving(false);
    }
  };

  const handleRemoveAllCategories = async () => {
    try {
      setOperationResult(null);
      setIsRemovingAll(true);
      
      console.log('Making API call to remove ALL categories with:', {
        url: `${API_BASE_URL}/api/assets/bulk/categorize`,
        assetIds: selectedAssetIds,
        addCategories: [],
        removeCategories: []
      });

      const response = await axiosInstance.put(`/api/assets/bulk/categorize`, {
        assetIds: selectedAssetIds,
        addCategories: [],
        removeCategories: 'ALL'
      });

      console.log('Remove ALL categories response:', response.data);

      setOperationResult({
        success: true,
        message: `All categories removed from ${response.data.modifiedCount} assets.`
      });
      
      setIsRemovingAll(false);
      setCategoryDialogOpen(false);
      
      onOperationComplete();
    } catch (error: any) {
      console.error('Error removing all categories:', error);
      setOperationResult({
        success: false,
        message: `Error removing all categories: ${error.message}`
      });
      setIsRemovingAll(false);
    }
  };

  const handleGenerateAI = async () => {
    try {
      setAiProcessing(true);
      setAiProcessingMessage("Starting AI analysis...");
      setOperationResult(null);

      console.log('Making API call to generate AI with:', {
        url: `${API_BASE_URL}/api/assets/bulk/generate-ai`,
        assetIds: selectedAssetIds
      });

      const response = await axiosInstance.post(`/api/assets/bulk/generate-ai`, {
        assetIds: selectedAssetIds
      });

      console.log('Generate AI response:', response.data);

      setAiProcessingMessage(response.data.message);
      
      setOperationResult({
        success: true,
        message: `AI analysis started for ${selectedAssetIds.length} assets. This may take some time to complete.`
      });
      
      setTimeout(() => {
        setAiProcessing(false);
        setAiProcessingMessage(null);
        onOperationComplete();
      }, 5000);
      
    } catch (error: any) {
      console.error('Error starting AI analysis:', error);
      setOperationResult({
        success: false,
        message: `Error starting AI analysis: ${error.message}`
      });
      setAiProcessing(false);
      setAiProcessingMessage(null);
    }
  };

  const handleRegenerateThumbnails = async () => {
    try {
      setThumbnailProcessing(true);
      setOperationResult(null);
      // Generate a client runId to correlate (set early for polling fallback)
      const runId = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      regenRunIdRef.current = runId;
      setRegenDialogOpen(true);
      setRegenProgress({ runId, current: 0, total: selectedAssetIds.length, success: 0, errors: 0, scope: 'selected' });
      setRegenLog(prev => [`Startar… total ${selectedAssetIds.length}`, ...prev]);

      // Prepare SSE listener for progress BEFORE starting the job
      try {
        const token = localStorage.getItem('authToken');
        const url = token ? `/api/assets/thumbnails/events?token=${encodeURIComponent(token)}` : '/api/assets/thumbnails/events';
        const es = new EventSource(url);
        regenEsRef.current = es;
        es.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (!data || data.scope !== 'selected') return;
            if (data.type === 'start') {
              // If backend generated runId, accept it
              regenRunIdRef.current = data.runId || regenRunIdRef.current;
              setRegenProgress({ runId: regenRunIdRef.current, current: 0, total: data.total || selectedAssetIds.length, success: 0, errors: 0, scope: 'selected' });
              setRegenDialogOpen(true);
              setRegenLog(prev => [`Startar… total ${data.total || selectedAssetIds.length}`, ...prev]);
            } else if (data.type === 'progress') {
              if (data.runId && regenRunIdRef.current && data.runId !== regenRunIdRef.current) return;
              setRegenProgress((prev: any) => ({
                runId: regenRunIdRef.current,
                current: data.current,
                total: data.total,
                success: data.success,
                errors: data.errors,
                fileName: data.fileName,
                etaSeconds: data.etaSeconds,
                scope: 'selected'
              }));
              if (data.fileName) setRegenLog(prev => [`${data.current}/${data.total} • ${data.fileName}`, ...prev].slice(0, 200));
            } else if (data.type === 'done') {
              if (data.runId && regenRunIdRef.current && data.runId !== regenRunIdRef.current) return;
              setRegenProgress((prev: any) => prev ? { ...prev, current: data.total, success: data.success, errors: data.errors } : null);
              setRegenTopToast({ success: true, message: `Thumbnails regenererade: OK ${data.success}, Fel ${data.errors}.` });
              setRegenLog(prev => [`Klart • OK ${data.success} • Fel ${data.errors}`, ...prev]);
              setTimeout(() => setRegenDialogOpen(false), 1200);
            }
          } catch {}
        };
        const startPolling = () => {
          if (regenPollIntervalRef.current || !regenRunIdRef.current) return;
          try {
            const token2 = localStorage.getItem('authToken');
            regenPollIntervalRef.current = setInterval(async () => {
              try {
                const status = await axiosInstance.get(`/api/assets/thumbnails/status`, { params: { runId: regenRunIdRef.current, token: token2 } });
                const s = status.data || {};
                if (!s || !s.total) return;
                setRegenProgress((prev: any) => ({
                  runId: regenRunIdRef.current,
                  current: s.current || 0,
                  total: s.total || (prev?.total || selectedAssetIds.length),
                  success: s.success || 0,
                  errors: s.errors || 0,
                  fileName: s.fileName,
                  etaSeconds: (() => {
                    const started = s.startedAt || 0;
                    const updated = s.updatedAt || Date.now();
                    const elapsed = Math.max(1, (updated - started) / 1000);
                    const rate = (s.current || 0) / elapsed;
                    const remaining = Math.max(0, (s.total || 0) - (s.current || 0));
                    return Math.round(remaining / Math.max(0.01, rate));
                  })(),
                  scope: 'selected'
                }));
                if (s.fileName) setRegenLog(prev => [`${s.current}/${s.total} • ${s.fileName}`, ...prev].slice(0, 200));
                if (s.done) {
                  clearInterval(regenPollIntervalRef.current);
                  regenPollIntervalRef.current = null;
                  setRegenTopToast({ success: true, message: `Thumbnails regenererade: OK ${s.success}, Fel ${s.errors}.` });
                  setRegenLog(prev => [`Klart • OK ${s.success} • Fel ${s.errors}`, ...prev]);
                  setTimeout(() => setRegenDialogOpen(false), 1200);
                }
              } catch {}
            }, 1000);
          } catch {}
        };
        // Start polling immediately and keep SSE in parallel
        startPolling();
        es.onerror = startPolling;
      } catch {}

      // Start the job
      console.log('Making API call to regenerate thumbnails with:', {
        url: `${API_BASE_URL}/api/assets/regenerate-thumbnails-selected`,
        assetIds: selectedAssetIds
      });

      const response = await axiosInstance.post(`/api/assets/regenerate-thumbnails-selected`, {
        assetIds: selectedAssetIds,
        runId
      });

      console.log('Regenerate thumbnails response:', response.data);
      // If SSE didn't close it, set success message here as well
      setRegenTopToast({ success: true, message: `Thumbnail regeneration complete for ${selectedAssetIds.length} assets. ${response.data.success} succeeded, ${response.data.errors} failed.` });
      setRegenLog(prev => [`Klart • OK ${response.data.success} • Fel ${response.data.errors}`, ...prev]);

      setTimeout(() => {
        setThumbnailProcessing(false);
        onOperationComplete();
        if (regenEsRef.current) { try { regenEsRef.current.close(); } catch {} }
        if (regenPollIntervalRef.current) { clearInterval(regenPollIntervalRef.current); regenPollIntervalRef.current = null; }
        setTimeout(() => setRegenDialogOpen(false), 800);
      }, 600);
      
    } catch (error: any) {
      console.error('Error regenerating thumbnails:', error);
      setRegenTopToast({ success: false, message: `Error regenerating thumbnails: ${error.response?.data?.message || error.message}` });
      setThumbnailProcessing(false);
      if (regenEsRef.current) { try { regenEsRef.current.close(); } catch {} }
      if (regenPollIntervalRef.current) { clearInterval(regenPollIntervalRef.current); regenPollIntervalRef.current = null; }
      setRegenDialogOpen(false);
      setRegenLog([]);
    }
  };

  React.useEffect(() => {
    const fetchTagSuggestions = async () => {
      try {
        const response = await axiosInstance.get(`/api/tags/suggestions`);
        setTagSuggestions(response.data || []);
      } catch (err) {
        console.error('Failed to fetch tag suggestions:', err);
      }
    };

    fetchTagSuggestions();
  }, []);

  const handleCategoriesChange = (newValue: ICategory[]) => {
    setQuickSelectedCategories(newValue);
  };

  const applyQuickCategories = async () => {
    if (quickSelectedCategories.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axiosInstance.post(`/api/assets/batch/categories`, {
        assetIds: selectedAssetIds,
        categoryIds: quickSelectedCategories.map(cat => cat._id)
      });
      
      setSuccess(`Categories applied to ${selectedAssetIds.length} assets`);
      onOperationComplete();
      setIsQuickCategoryDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply categories');
    } finally {
      setLoading(false);
    }
  };

  const applyTags = async () => {
    if (!tagsInput.trim()) return;
    
    setLoading(true);
    setError(null);
    
    const tags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    try {
      await axiosInstance.post(`/api/assets/batch/tags`, {
        assetIds: selectedAssetIds,
        tags: tags
      });
      
      setSuccess(`Tags applied to ${selectedAssetIds.length} assets`);
      onOperationComplete();
      setIsTagDialogOpen(false);
      setTagsInput('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply tags');
    } finally {
      setLoading(false);
    }
  };

  const loadBadges = async () => {
    try {
      const badges = await getBadges();
      setAvailableBadges(badges.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (err: any) {
      setError('Failed to load badges: ' + err.message);
    }
  };

  const loadBadgeOverview = async () => {
    if (selectedAssetIds.length === 0) {
      setBadgeCounts({});
      return;
    }
    setBadgeOverviewLoading(true);
    try {
      const results = await Promise.all(
        selectedAssetIds.map(async (id) => {
          try {
            const resp = await axiosInstance.get(`/api/assets/${id}`);
            const active = Array.isArray(resp.data?.activeBadges) ? resp.data.activeBadges : [];
            return active
              .map((badge: any) => (typeof badge === 'string' ? badge : badge?._id))
              .filter((badgeId: string | undefined) => !!badgeId);
          } catch {
            return [];
          }
        })
      );
      const counts: Record<string, number> = {};
      results.forEach((badgeList: string[]) => {
        badgeList.forEach((badgeId: string) => {
          counts[badgeId] = (counts[badgeId] || 0) + 1;
        });
      });
      setBadgeCounts(counts);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load badge overview');
    } finally {
      setBadgeOverviewLoading(false);
    }
  };

  const getBadgeStatus = (badgeId: string): 'all' | 'some' | 'none' => {
    const total = selectedAssetIds.length;
    if (total === 0) return 'none';
    const count = badgeCounts[badgeId] || 0;
    if (count === 0) return 'none';
    if (count === total) return 'all';
    return 'some';
  };

  const getBadgeStatusText = (badgeId: string) => {
    const total = selectedAssetIds.length;
    if (total === 0) return '';
    const count = badgeCounts[badgeId] || 0;
    if (count === 0) return 'Ingen av de valda';
    if (count === total) return 'Alla valda';
    return `${count}/${total} valda`;
  };

  const getBadgeActionValue = (badgeId: string): BadgeActionSelection => {
    return badgeActions[badgeId] || 'none';
  };

  const handleBadgeActionChange = (badgeId: string, action: BadgeActionSelection) => {
    setBadgeActions(prev => {
      const next = { ...prev };
      if (action === 'none') {
        delete next[badgeId];
      } else {
        next[badgeId] = action;
      }
      return next;
    });
  };

  const applyBadges = async () => {
    const badgesToAdd = Object.entries(badgeActions)
      .filter(([, action]) => action === 'add')
      .map(([badgeId]) => badgeId);
    const badgesToRemove = Object.entries(badgeActions)
      .filter(([, action]) => action === 'remove')
      .map(([badgeId]) => badgeId);

    if (badgesToAdd.length === 0 && badgesToRemove.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axiosInstance.post(`/api/assets/batch/badges`, {
        assetIds: selectedAssetIds,
        addBadgeIds: badgesToAdd,
        removeBadgeIds: badgesToRemove
      });
      
      setSuccess(`Badges updated for ${selectedAssetIds.length} assets`);
      onOperationComplete();
      setIsBadgeDialogOpen(false);
      setBadgeActions({});
      setBadgeCounts({});
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update badges');
    } finally {
      setLoading(false);
    }
  };

  const batchRename = async () => {
    if (!renamePattern.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axiosInstance.post(`/api/assets/batch/rename`, {
        assetIds: selectedAssetIds,
        pattern: renamePattern
      });
      
      setSuccess(`Renamed ${selectedAssetIds.length} assets`);
      onOperationComplete();
      setIsRenameDialogOpen(false);
      setRenamePattern('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to rename assets');
    } finally {
      setLoading(false);
    }
  };

  const deleteAssets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await axiosInstance.post(`/api/assets/batch/delete`, {
        assetIds: selectedAssetIds
      });
      
      setSuccess(`Deleted ${selectedAssetIds.length} assets`);
      onOperationComplete();
      onClearSelection();
      setIsDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete assets');
    } finally {
      setLoading(false);
    }
  };

  const deleteAssetsWithWordPressUnpublish = async () => {
    await requestWordPressArchiveUnpublish(deleteWordPressImpacts);
    await deleteAssets();
  };

  const openDeleteImpactDialog = async () => {
    try {
      const impacts = await getWordPressArchiveUnpublishImpacts(selectedAssetIds);
      setDeleteWordPressImpacts(impacts);
      setIsDeleteImpactDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Kunde inte kontrollera WordPress-publicering.');
    }
  };

  const downloadAsZip = async () => {
    setLoading(true);
    
    try {
      const response = await axiosInstance.post(
        `/api/assets/batch/download`, 
        { assetIds: selectedAssetIds },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assets-${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Download started');
    } catch (err) {
      setError('Failed to download assets');
    } finally {
      setLoading(false);
    }
  };

  const startValidooSync = async () => {
    validooCancelRef.current = false;
    setIsValidooSyncing(true);
    setOperationResult(null);
    setValidooLog([]);

    let successCount = 0;
    let failedCount = 0;

    // Pre-filter: exclude assets flagged to skip Validoo sync
    // Also build a map of assetId -> artikelnummer for logging
    let idsToSync = selectedAssetIds;
    const artikelnummerMap: Record<string, string> = {};
    
    // Helper to extract artikelnummer from metadata (case-insensitive)
    const getArtikelnummer = (asset: any): string => {
      if (!asset?.metadata) return '';
      const metadata = asset.metadata;
      // Try common variations of artikelnummer key
      const keys = ['artikelnummer', 'Artikelnummer', 'ARTIKELNUMMER', 'articleNumber', 'ArticleNumber', 'artnr', 'ArtNr'];
      for (const key of keys) {
        if (metadata[key] && typeof metadata[key] === 'string' && metadata[key].trim()) {
          return metadata[key].trim();
        }
      }
      // Fallback: check all keys case-insensitively
      const lowerKeys = Object.keys(metadata);
      for (const k of lowerKeys) {
        if (k.toLowerCase() === 'artikelnummer' && metadata[k]) {
          return String(metadata[k]).trim();
        }
      }
      return '';
    };
    
    try {
      if (selectedAssetIds.length > 0) {
        // Fetch in chunks to avoid URL length limits with many selected assets
        const CHUNK_SIZE = 50;
        const allPrefetched: any[] = [];
        for (let i = 0; i < selectedAssetIds.length; i += CHUNK_SIZE) {
          const chunk = selectedAssetIds.slice(i, i + CHUNK_SIZE);
          const response = await axiosInstance.get(`/api/assets`, { params: { assetIds: chunk.join(',') } });
          if (Array.isArray(response.data)) {
            allPrefetched.push(...response.data);
          }
        }
        const assets = allPrefetched;
        
        // Build artikelnummer map for all assets
        assets.forEach((a: any) => {
          if (a._id) {
            artikelnummerMap[a._id] = getArtikelnummer(a);
          }
        });
        
        const notFlagged = assets.filter((a: any) => !a.skipValidooSync).map((a: any) => a._id);
        const skippedCount = selectedAssetIds.length - notFlagged.length;
        idsToSync = notFlagged;
        if (skippedCount > 0) {
          setValidooLog(prev => [...prev, `↪ Skipping ${skippedCount} asset(s) flagged to not sync with Validoo`]);
        }
      }
    } catch (e) {
      // On failure to prefetch, fall back to original list
    }

    setValidooProgress({ current: 0, total: idsToSync.length, successCount: 0, failedCount: 0 });

    for (let index = 0; index < idsToSync.length; index++) {
      if (validooCancelRef.current) break;
      const assetId = idsToSync[index];
      const artikelnummer = artikelnummerMap[assetId] || '';
      // Format: show artikelnummer first if available, then assetId in parentheses
      const assetLabel = artikelnummer ? `${artikelnummer} (${assetId})` : assetId;
      
      try {
        const response = await axiosInstance.post(`/api/external/sync-asset/${assetId}?system=validoo`);
        if (response.data?.success) {
          successCount += 1;
          setValidooLog(prev => [...prev, `✔ Synced ${assetLabel}`]);
        } else {
          failedCount += 1;
          setValidooLog(prev => [...prev, `✖ Failed ${assetLabel}: ${response.data?.message || 'Unknown error'}`]);
        }
      } catch (err: any) {
        failedCount += 1;
        setValidooLog(prev => [...prev, `✖ Error ${assetLabel}: ${err.response?.data?.message || err.message}`]);
      } finally {
        setValidooProgress({ current: index + 1, total: idsToSync.length, successCount, failedCount });
      }

      if (validooCancelRef.current) break;
      await sleep(200);
    }

    setIsValidooSyncing(false);
    if (validooCancelRef.current) {
      setOperationResult({ success: true, message: `Validoo sync canceled. Processed ${validooProgress.current} of ${validooProgress.total}. OK: ${successCount}, Failed: ${failedCount}` });
    } else {
      setOperationResult({ success: failedCount === 0, message: `Validoo sync complete. OK: ${successCount}, Failed: ${failedCount}` });
    }
  };

  const cancelValidooSync = () => {
    validooCancelRef.current = true;
  };

  const copyValidooLog = async () => {
    try {
      await navigator.clipboard.writeText(validooLog.join('\n'));
      setSuccess('Log copied to clipboard');
    } catch (e) {
      setError('Failed to copy log');
    }
  };

  const exportValidooLog = () => {
    const blob = new Blob([validooLog.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `validoo-sync-log-${Date.now()}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const openCsvExport = () => {
    try {
      localStorage.setItem('csvExportSelectedAssetIds', JSON.stringify(selectedAssetIds));
      onOpenCsvSettings?.();
      setSuccess('Selection sent to CSV Export');
    } catch (e) {}
  };

  const openBulkEditDrawer = () => {
    const ev = new CustomEvent('open-bulk-edit-drawer', { detail: { selectedAssetIds } });
    window.dispatchEvent(ev);
  };

  const handleStartProductImageValidation = async () => {
    await productImageValidationRunner.startRun('selected', selectedAssetIds);
  };

  const actions: BulkActionDefinition[] = [
    {
      id: 'badges',
      permission: 'assets:manageBadges',
      label: 'Redigera badges',
      compactLabel: 'Badges',
      variant: 'outlined',
      color: 'primary',
      icon: <LabelIcon fontSize="small" />,
      onClick: () => setIsBadgeDialogOpen(true),
      disabled: loading,
      compactPrimary: false,
    },
    {
      id: 'downloadZip',
      permission: 'assets:download',
      label: 'Ladda ner som ZIP',
      compactLabel: 'ZIP',
      variant: 'outlined',
      color: 'primary',
      icon: <DownloadIcon fontSize="small" />,
      onClick: downloadAsZip,
      disabled: loading,
      compactPrimary: true,
    },
    {
      id: 'exportCsv',
      permission: 'csv:export',
      label: 'Exportera CSV (valda)',
      compactLabel: 'CSV',
      variant: 'outlined',
      color: 'primary',
      icon: <TableViewIcon fontSize="small" />,
      onClick: openCsvExport,
      disabled: loading || selectedAssetIds.length === 0,
      compactPrimary: true,
    },
    {
      id: 'barcode',
      permission: ['assets:update', 'barcode:access'],
      label: 'Generera EAN-koder',
      compactLabel: 'EAN-koder',
      variant: 'outlined',
      color: 'primary',
      icon: <QrCode2Icon fontSize="small" />,
      onClick: () => setBarcodeDialogOpen(true),
      disabled: loading || selectedAssetIds.length === 0,
      compactPrimary: false,
    },
    {
      id: 'regenerateThumbnails',
      permission: 'assets:update',
      label: 'Regenerera miniatyr',
      compactLabel: 'Miniatyrer',
      variant: 'outlined',
      color: 'primary',
      icon: isThumbnailProcessing ? <CircularProgress size={16} /> : <ImageIcon fontSize="small" />,
      onClick: handleRegenerateThumbnails,
      disabled: isThumbnailProcessing || loading,
      compactPrimary: false,
    },
    {
      id: 'generateAi',
      permission: 'assets:runAIAnalysis',
      label: 'Generera AI',
      compactLabel: 'AI',
      variant: 'outlined',
      color: 'primary',
      icon: isAiProcessing ? <CircularProgress size={16} /> : <SmartToyIcon fontSize="small" />,
      onClick: handleGenerateAI,
      disabled: isAiProcessing || isThumbnailProcessing,
      compactPrimary: false,
    },
    {
      id: 'athenaVisualProfile',
      permission: 'admin:all',
      label: 'Skapa Athena-bildprofil',
      compactLabel: 'Athena-profil',
      variant: 'outlined',
      color: 'primary',
      icon: <AthenaVisualProfileIcon />,
      onClick: () => window.dispatchEvent(new Event(OPEN_ATHENA_VISUAL_ENRICHMENT_EVENT)),
      disabled: loading || selectedAssetIds.length === 0,
      compactPrimary: false,
    },
    {
      id: 'validoo',
      permission: 'admin:all',
      label: 'Synka Validoo',
      compactLabel: 'Validoo',
      variant: 'outlined',
      color: 'primary',
      icon: isValidooSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />,
      onClick: () => setIsValidooDialogOpen(true),
      disabled: isValidooSyncing || loading,
      compactPrimary: false,
    },
    {
      id: 'validateProductImages',
      permission: 'system:runProductImageValidation',
      label: 'Testa valda bilder',
      compactLabel: 'Bildtest',
      variant: 'outlined',
      color: 'primary',
      icon: productImageValidationRunner.isRunning ? <CircularProgress size={16} /> : <FactCheckIcon fontSize="small" />,
      onClick: handleStartProductImageValidation,
      disabled: productImageValidationRunner.isRunning || loading || selectedAssetIds.length === 0,
      compactPrimary: false,
    },
    {
      id: 'manageCategories',
      permission: 'assets:update',
      label: 'Hantera kategorier',
      compactLabel: 'Kategorier',
      variant: 'outlined',
      color: 'primary',
      icon: <CategoryIcon fontSize="small" />,
      onClick: () => setCategoryDialogOpen(true),
      compactPrimary: true,
    },
    {
      id: 'bulkEdit',
      permission: 'assets:update',
      label: 'Massredigera',
      compactLabel: 'Massredigera',
      variant: 'contained',
      color: 'primary',
      icon: <TuneIcon fontSize="small" />,
      onClick: openBulkEditDrawer,
      compactPrimary: true,
    },
    {
      id: 'delete',
      permission: 'assets:delete',
      label: 'Ta bort',
      compactLabel: 'Ta bort',
      variant: 'contained',
      color: 'error',
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setIsDeleteDialogOpen(true),
      disabled: loading,
      compactPrimary: true,
      destructive: true,
    },
  ];

  const visibleActions = actions.filter((action) =>
    Array.isArray(action.permission)
      ? hasAllPermissions(currentUser ?? null, action.permission)
      : hasPermission(currentUser ?? null, action.permission)
  );
  const primaryActions = isCompactTabletLayout
    ? visibleActions.filter((action) => action.compactPrimary)
    : visibleActions;
  const secondaryActions = isCompactTabletLayout
    ? visibleActions.filter((action) => !action.compactPrimary)
    : [];

  const renderActionButton = (action: BulkActionDefinition, compact: boolean) => (
    <Button
      key={action.id}
      variant={action.variant}
      color={action.color}
      size="small"
      startIcon={action.icon}
      onClick={action.onClick}
      disabled={action.disabled}
      sx={compact ? compactActionButtonSx : actionButtonSx}
    >
      {compact ? action.compactLabel ?? action.label : action.label}
    </Button>
  );

  const renderSecondaryAction = (action: BulkActionDefinition) => (
    <MenuItem
      key={action.id}
      onClick={() => {
        closeMoreActionsMenu();
        action.onClick();
      }}
      disabled={action.disabled}
      sx={action.destructive ? { color: 'error.main' } : undefined}
    >
      <ListItemIcon sx={action.destructive ? { color: 'error.main' } : undefined}>
        {action.icon}
      </ListItemIcon>
      <ListItemText
        primary={action.label}
        primaryTypographyProps={{ fontSize: '0.85rem' }}
      />
    </MenuItem>
  );

  React.useEffect(() => {
    if (isCompactTabletLayout && secondaryActions.length > 0) return;
    closeMoreActionsMenu();
  }, [isCompactTabletLayout, secondaryActions.length]);

  React.useEffect(() => {
    if (!onHeightChange) return;
    const element = barRef.current;
    if (!element) return;

    const reportHeight = () => {
      onHeightChange(element.offsetHeight);
    };

    reportHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => reportHeight());
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', reportHeight);
    return () => window.removeEventListener('resize', reportHeight);
  }, [onHeightChange, isCompactTabletLayout, operationResult, loading]);

  React.useEffect(() => {
    return () => {
      onHeightChange?.(0);
    };
  }, [onHeightChange]);

  React.useEffect(() => {
    if (!operationResult) return;
    const timer = window.setTimeout(() => {
      setOperationResult(null);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [operationResult]);

  return (
    <Paper
      ref={barRef}
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        px: 1.5,
        pt: 1.5,
        pb: 1.25,
        minHeight: 56,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <VisualEnrichmentBulkAction selectedAssetIds={selectedAssetIds} />
      {isCompactTabletLayout ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'rgba(0,0,0,0.75)',
                whiteSpace: 'nowrap'
              }}
            >
              {selectedAssetIds.length} valda filer
            </Typography>
            <Tooltip title="Rensa markering">
              <IconButton size="small" onClick={onClearSelection} aria-label="Rensa markering">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {primaryActions.map((action) => renderActionButton(action, true))}
            {secondaryActions.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<MoreHorizIcon fontSize="small" />}
                onClick={(event) => setMoreActionsAnchorEl(event.currentTarget)}
                sx={compactActionButtonSx}
              >
                Fler
              </Button>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.75)',
              whiteSpace: 'nowrap'
            }}
          >
            {selectedAssetIds.length} valda filer
          </Typography>
          <Tooltip title="Rensa markering">
            <IconButton size="small" onClick={onClearSelection} aria-label="Rensa markering">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              flex: 1,
              minWidth: 0,
              pr: 1
            }}
          >
            {primaryActions.map((action) => renderActionButton(action, false))}
          </Box>
        </Box>
      )}

      {operationResult && (
        <Alert 
          severity={operationResult.success ? "success" : "error"} 
          sx={{
            py: 0.5,
            px: 1,
            '& .MuiAlert-message': { fontSize: '0.78rem' }
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setOperationResult(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {operationResult.message}
        </Alert>
      )}
      
      {loading && (
        <LinearProgress sx={{ mt: 0.5 }} />
      )}

      <Menu
        anchorEl={moreActionsAnchorEl}
        open={Boolean(moreActionsAnchorEl)}
        onClose={closeMoreActionsMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        MenuListProps={{ dense: true }}
      >
        {secondaryActions.map(renderSecondaryAction)}
      </Menu>
      
      <Dialog open={isQuickCategoryDialogOpen} onClose={() => setIsQuickCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Categories</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select categories to apply to the {selectedAssetIds.length} selected assets:
          </Typography>
          <Autocomplete
            multiple
            options={categories}
            getOptionLabel={(option) => option.name}
            value={quickSelectedCategories}
            onChange={(_, newValue) => handleCategoriesChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Categories"
                placeholder="Select categories"
                fullWidth
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option._id}
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsQuickCategoryDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={applyQuickCategories} 
            color="primary" 
            disabled={quickSelectedCategories.length === 0 || loading}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isTagDialogOpen} onClose={() => setIsTagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tags</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter tags to apply to the {selectedAssetIds.length} selected assets:
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={tagSuggestions}
            value={tagsInput ? tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : []}
            onChange={(_, newValue) => setTagsInput(newValue.join(', '))}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tags"
                placeholder="Enter tags (comma separated)"
                fullWidth
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Chip
                  label={tag}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
          />
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Separate multiple tags with commas
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTagDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={applyTags} 
            color="primary" 
            disabled={!tagsInput.trim() || loading}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate thumbnails progress dialog */}
      {/* Top persistent toast for regeneration result */}
      {regenTopToast && (
        <Alert
          severity={regenTopToast.success ? 'success' : 'error'}
          sx={{ position: 'fixed', top: '64px', left: '50%', transform: 'translateX(-50%)', zIndex: 1300, width: 'auto', maxWidth: '80%' }}
          action={
            <>
              <Button color="inherit" size="small" onClick={() => { setShowRegenLog(true); setRegenDialogOpen(true); }}>Visa logg</Button>
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setRegenTopToast(null)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </>
          }
        >
          {regenTopToast.message}
        </Alert>
      )}

      <Dialog open={regenDialogOpen} onClose={() => setRegenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenererar thumbnails…</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant={regenProgress?.total ? 'determinate' : 'indeterminate'} value={regenProgress?.total ? (Math.min(regenProgress.current, regenProgress.total) / regenProgress.total) * 100 : 0} />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
              {regenProgress ? `${Math.min(regenProgress.current, regenProgress.total)}/${regenProgress.total}` : ''}
            </Typography>
          </Box>
          {regenProgress?.fileName && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
              {regenProgress.fileName}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            {regenProgress?.etaSeconds != null ? `≈ ${Math.max(0, regenProgress.etaSeconds)}s kvar` : ''}
            {regenProgress ? ` • OK: ${regenProgress.success} • Fel: ${regenProgress.errors}` : ''}
          </Typography>
          {showRegenLog && (
            <Box sx={{ mt: 1, maxHeight: 180, overflow: 'auto', bgcolor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '4px', p: 1 }}>
              {regenLog.length === 0 ? (
                <Typography variant="caption" color="textSecondary">Inga loggrader ännu…</Typography>
              ) : (
                regenLog.map((row, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: 'block' }}>{row}</Typography>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegenLog(v => !v)}>{showRegenLog ? 'Dölj logg' : 'Visa logg'}</Button>
          <Button onClick={() => setRegenDialogOpen(false)}>Dölj</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isBadgeDialogOpen} onClose={() => setIsBadgeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Badges</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add or remove badges for the {selectedAssetIds.length} selected assets.
          </Typography>
          
          {availableBadges.length === 0 || badgeOverviewLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {availableBadges.map((badge) => {
                const statusText = getBadgeStatusText(badge._id);
                const currentAction = getBadgeActionValue(badge._id);
                const removeDisabled = getBadgeStatus(badge._id) === 'none';
                return (
                  <Box 
                    key={badge._id} 
                    sx={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      p: 1.5, 
                      backgroundColor: 'white' 
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">{badge.name}</Typography>
                        <Chip
                          label={badge.name}
                          size="small"
                          style={{
                            backgroundColor: badge.color,
                            color: badge.textColor,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {statusText}
                      </Typography>
                    </Box>
                    <ToggleButtonGroup
                      value={currentAction}
                      exclusive
                      size="small"
                      onChange={(_, value) => handleBadgeActionChange(badge._id, (value || 'none') as BadgeActionSelection)}
                    >
                      <ToggleButton value="none">Behåll</ToggleButton>
                      <ToggleButton value="add">Lägg till</ToggleButton>
                      <ToggleButton value="remove" disabled={removeDisabled}>Ta bort</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBadgeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={applyBadges} 
            color="primary" 
            disabled={Object.keys(badgeActions).length === 0 || loading}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isRenameDialogOpen} onClose={() => setIsRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Batch Rename</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter a pattern to rename the {selectedAssetIds.length} selected assets:
          </Typography>
          <TextField
            variant="outlined"
            label="Rename Pattern"
            placeholder="e.g., Project_{index}"
            fullWidth
            value={renamePattern}
            onChange={(e) => setRenamePattern(e.target.value)}
            helperText="Use {index} for sequential numbering, {originalName} for the original filename"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRenameDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={batchRename} 
            color="primary" 
            disabled={!renamePattern.trim() || loading}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isValidooDialogOpen} onClose={() => { if (!isValidooSyncing) setIsValidooDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Validoo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {selectedAssetIds.length} assets will be synchronized with Validoo.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant={validooProgress.total > 0 ? 'determinate' : 'indeterminate'} value={validooProgress.total > 0 ? (validooProgress.current / validooProgress.total) * 100 : 0} />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
              {validooProgress.current}/{validooProgress.total}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            OK: {validooProgress.successCount} • Failed: {validooProgress.failedCount}
          </Typography>
          <Box sx={{ maxHeight: 180, overflow: 'auto', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '4px', p: 1 }}>
            {validooLog.length === 0 ? (
              <Typography variant="caption" color="textSecondary">No messages yet.</Typography>
            ) : (
              validooLog.map((row, idx) => (
                <Typography key={idx} variant="caption" sx={{ display: 'block' }}>{row}</Typography>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsValidooLogDialogOpen(true)} disabled={validooLog.length === 0}>Show detailed log</Button>
          <Box sx={{ flex: 1 }} />
          {isValidooSyncing && (
            <Button onClick={cancelValidooSync} color="error">Cancel</Button>
          )}
          <Button onClick={() => setIsValidooDialogOpen(false)} disabled={isValidooSyncing}>Close</Button>
          <Button onClick={startValidooSync} color="primary" disabled={isValidooSyncing} startIcon={isValidooSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}>Start</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isValidooLogDialogOpen} onClose={() => setIsValidooLogDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Validoo Sync Log</DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 400, overflow: 'auto', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '4px', p: 1 }}>
            {validooLog.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No log entries.</Typography>
            ) : (
              validooLog.map((row, idx) => (
                <Typography key={idx} variant="body2" sx={{ display: 'block' }}>{row}</Typography>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={copyValidooLog}>Copy</Button>
          <Button onClick={exportValidooLog}>Export</Button>
          <Button onClick={() => setIsValidooLogDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete Assets</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete {selectedAssetIds.length} assets? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false);
              void openDeleteImpactDialog();
            }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <RelationImpactDialog
        open={isDeleteImpactDialogOpen}
        action="delete"
        assetIds={selectedAssetIds}
        wordPressImpacts={deleteWordPressImpacts}
        onClose={() => {
          setIsDeleteImpactDialogOpen(false);
          setDeleteWordPressImpacts([]);
        }}
        onConfirm={deleteAssets}
        onConfirmWithWordPressUnpublish={deleteAssetsWithWordPressUnpublish}
      />

      <BulkBarcodeDialog
        open={isBarcodeDialogOpen}
        onClose={() => setBarcodeDialogOpen(false)}
        selectedAssetIds={selectedAssetIds}
        categories={categories}
        onComplete={onOperationComplete}
      />
      <ProductImageValidationRunDialog
        open={productImageValidationRunner.dialogOpen}
        title="Bildkontroll Validoo"
        job={productImageValidationRunner.job}
        run={productImageValidationRunner.run}
        error={productImageValidationRunner.error}
        starting={productImageValidationRunner.starting}
        loadingRun={productImageValidationRunner.loadingRun}
        onClose={productImageValidationRunner.closeDialog}
        onDownload={(format) => void productImageValidationRunner.downloadRun(format)}
      />
      
      <Dialog 
        open={isCategoryDialogOpen} 
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Manage Categories for {selectedAssetIds.length} Assets</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={categoryMode}
              exclusive
              onChange={(_, v) => v && setCategoryMode(v)}
              size="small"
            >
              <ToggleButton value="add">Add</ToggleButton>
              <ToggleButton value="remove">Remove</ToggleButton>
              <ToggleButton value="replace">Replace</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Search categories…"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Typography variant="body2" sx={{ ml: 'auto' }}>
              Selected: {selectedCategories.length}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ maxHeight: 420, overflow: 'auto', pr: 1 }}>
            {overviewLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading current category status…</Typography>
              </Box>
            )}
            {categories
              .filter(category => !category.parentCategory)
              .map((mainCategory) => {
                const children = categories.filter(subCategory => {
                  if (typeof subCategory.parentCategory === 'string') {
                    return subCategory.parentCategory === mainCategory._id;
                  } else if (subCategory.parentCategory && typeof subCategory.parentCategory === 'object') {
                    return (subCategory.parentCategory as {_id: string})._id === mainCategory._id;
                  }
                  return false;
                }).filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase()));
                const mainVisible = mainCategory.name.toLowerCase().includes(categorySearch.toLowerCase());
                if (!mainVisible && children.length === 0) return null;
                return (
                  <Box key={mainCategory._id} sx={{ mb: 2 }}>
                    <FormGroup>
                      {mainVisible && (
                        <FormControlLabel
                          key={mainCategory._id}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedCategories.includes(mainCategory._id)}
                              onChange={() => handleCategoryToggle(mainCategory._id)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>{mainCategory.name}</Typography>
                              <Chip 
                                size="small" 
                                label={getCategoryStatus(mainCategory._id) === 'all' ? 'All' : getCategoryStatus(mainCategory._id) === 'some' ? 'Some' : 'None'}
                                color={getCategoryStatus(mainCategory._id) === 'all' ? 'success' : getCategoryStatus(mainCategory._id) === 'some' ? 'warning' : 'default'}
                                variant="outlined"
                              />
                            </Box>
                          }
                        />
                      )}
                    </FormGroup>
                    <FormGroup>
                      {children.map((sub) => (
                        <FormControlLabel
                          key={sub._id}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedCategories.includes(sub._id)}
                              onChange={() => handleCategoryToggle(sub._id)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">{sub.name}</Typography>
                              <Chip 
                                size="small" 
                                label={getCategoryStatus(sub._id) === 'all' ? 'All' : getCategoryStatus(sub._id) === 'some' ? 'Some' : 'None'}
                                color={getCategoryStatus(sub._id) === 'all' ? 'success' : getCategoryStatus(sub._id) === 'some' ? 'warning' : 'default'}
                                variant="outlined"
                              />
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Box>
                );
              })}
          </Box>
          <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
            <Typography variant="body2">
              Mode: <strong>{categoryMode.toUpperCase()}</strong>
              {categoryMode === 'replace' && selectedCategories.length === 0 ? ' (this will remove all categories)' : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button 
            color="error"
            variant="outlined"
            startIcon={<WarningAmberIcon />}
            sx={{ mr: 'auto' }}
            onClick={() => {
              const ok = window.confirm('Wipe: Remove ALL categories from selected assets? This cannot be undone.');
              if (!ok) return;
              handleRemoveAllCategories();
            }}
          >
            Wipe (Remove All)
          </Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              if (categoryMode === 'replace' && selectedCategories.length === 0) {
                const ok = window.confirm('This will remove ALL categories from the selected assets. Continue?');
                if (!ok) return;
              }
              if (categoryMode === 'add') {
                await handleApplyCategories();
              } else if (categoryMode === 'remove') {
                await handleRemoveCategories();
              } else {
                try {
                  setOperationResult(null);
                  const addNames = selectedCategories.map(catId => 
                    categories.find(cat => cat._id === catId)?.name || ''
                  ).filter(Boolean);
                  const response = await axiosInstance.put(`/api/assets/bulk/categorize`, {
                    assetIds: selectedAssetIds,
                    addCategories: addNames,
                    removeCategories: 'ALL'
                  });
                  setOperationResult({ success: true, message: response.data?.message || 'Categories replaced' });
                  setCategoryDialogOpen(false);
                  onOperationComplete();
                } catch (err: any) {
                  setOperationResult({ success: false, message: err.response?.data?.message || 'Failed to replace categories' });
                }
              }
            }}
            disabled={categoryMode !== 'replace' && selectedCategories.length === 0}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(error) || Boolean(success)}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ marginTop: '80px' }}
      >
        <Alert 
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          elevation={6}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default BulkOperationBar; 